
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import { AccountType } from "@/types/payment";
import { PaymentTabType } from "./types";
import { safeSelect, safeInsert } from "@/utils/supabaseHelpers";

// Define the type for the payment methods database record
interface PaymentMethodRecord {
  id: string;
  profile_id: string;
  bank_name: string | null;
  bank_code: string | null;
  branch_name: string | null;
  branch_code: string | null;
  account_type: AccountType | null;
  account_number: string | null;
  account_holder_name: string | null;
  paypay_username: string | null;
  paypay_qr_url: string | null;
  is_preferred: boolean;
}

export const usePaymentMethods = () => {
  const savePaymentMethods = async (
    userId: string,
    paymentTab: PaymentTabType,
    bankName: string,
    bankCode: string,
    branchName: string,
    branchCode: string,
    accountType: AccountType,
    accountNumber: string,
    accountHolderName: string,
    paypayUsername: string,
    isPreferred: boolean = false
  ) => {
    try {
      // First check if the user already has a payment method
      const { data: existingMethod, error } = await safeSelect<PaymentMethodRecord>(
        'payment_methods',
        { profile_id: userId },
        { single: true }
      );

      if (paymentTab === "bank" && (bankName || branchName || accountNumber || accountHolderName)) {
        if (existingMethod) {
          // Update existing payment method
          const { error: updateError } = await supabase
            .from('payment_methods')
            .update({
              bank_name: bankName,
              bank_code: bankCode,
              branch_name: branchName,
              branch_code: branchCode,
              account_type: accountType,
              account_number: accountNumber,
              account_holder_name: accountHolderName,
              is_preferred: isPreferred
            })
            .eq('id', existingMethod.id);
            
          if (updateError) {
            console.error('Error updating bank payment method:', updateError);
            throw updateError;
          }
        } else {
          // Insert new payment method
          const { error: insertError } = await supabase
            .from('payment_methods')
            .insert({
              profile_id: userId,
              bank_name: bankName,
              bank_code: bankCode,
              branch_name: branchName,
              branch_code: branchCode,
              account_type: accountType,
              account_number: accountNumber,
              account_holder_name: accountHolderName,
              is_preferred: isPreferred
            });
            
          if (insertError) {
            console.error('Error saving bank payment method:', insertError);
            throw insertError;
          }
        }
      } else if (paymentTab === "paypay" && paypayUsername) {
        if (existingMethod) {
          // Update existing payment method
          const { error: updateError } = await supabase
            .from('payment_methods')
            .update({
              paypay_username: paypayUsername,
              is_preferred: isPreferred
            })
            .eq('id', existingMethod.id);
            
          if (updateError) {
            console.error('Error updating PayPay payment method:', updateError);
            throw updateError;
          }
        } else {
          // Insert new payment method
          const { error: insertError } = await supabase
            .from('payment_methods')
            .insert({
              profile_id: userId,
              paypay_username: paypayUsername,
              is_preferred: isPreferred
            });
            
          if (insertError) {
            console.error('Error saving PayPay payment method:', insertError);
            throw insertError;
          }
        }
      }
      return true;
    } catch (error) {
      console.error('Error saving payment methods:', error);
      toast({
        title: "Warning",
        description: "Your account was created, but we couldn't save your payment methods. You can add them later in settings.",
        variant: "destructive"
      });
      return false;
    }
  };

  return {
    savePaymentMethods
  };
};
